import React, { memo, useCallback, useMemo } from "react";

import { DrawerContent } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import {
  GenerateTaskQuery,
  GenerateTaskQueryVariables,
  MessageFragment,
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
  MessageThreadType,
  TaskPriorityType,
  TaskStatusType,
} from "gql/graphql";
import { usePanelSize } from "modules/providers/panel-size-provider";
import { TASK_DRAFT_ID } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import useBurstQuery from "modules/hooks/use-burst-query";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import GENERATE_TASK_QUERY from "modules/messaging/_networking/generate-task";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import DrawerTopHeader from "./drawer-top-header";
import ForwardToSelectMessages from "./forward-to/forward-to-select-messages";
import DeltaBuilder from "modules/quill/delta-builder";

const GenerateTaskFromMessagesDrawer: React.FC = () => {
  const { drawerState, openDrawer } = useOverlay();
  const { rightWidth } = usePanelSize();

  const messageThreadId = drawerState?.id;

  const { data: messageThreadsData } = useQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY, {
    variables: { id: messageThreadId ? [messageThreadId] : [] },
    skip: !messageThreadId,
  });

  const messageThread = useMemo(
    () =>
      messageThreadsData?.messageThreads.length === 1
        ? messageThreadsData.messageThreads[0]
        : undefined,
    [messageThreadsData],
  );

  const { setIsLocked, setSubject, setDelta, createDraft } =
    useMessageThreadDraft({
      draftId: TASK_DRAFT_ID,
    });

  const [burstGenerateTaskQuery, { loading: isGenerateTaskLoading }] =
    useBurstQuery<GenerateTaskQuery, GenerateTaskQueryVariables>(
      GENERATE_TASK_QUERY,
    );

  const onNextClicked = useCallback(
    async (messages?: MessageFragment[]) => {
      if (isGenerateTaskLoading || !messageThread) return;

      const messageIdList = messages?.map((m) => m.id) ?? [];
      if (!messageIdList.length) return;

      let generated: GenerateTaskQuery["generateTask"] | undefined;
      try {
        const { data } = await burstGenerateTaskQuery({
          fetchPolicy: "no-cache",
          variables: {
            input: { messageIdList },
          },
        });
        generated = data?.generateTask;
      } catch {
        return;
      }

      if (!generated) return;

      await createDraft(
        {
          type: MessageThreadType.Task,
          draftType: "new-thread",
          task: {
            assignedUserId: generated.assignedUserId ?? undefined,
            dueDateOption: "Custom",
            dueTime: generated.dueTime ?? undefined,
            priorityType: generated.priorityType ?? TaskPriorityType.None,
            statusType: TaskStatusType.NotStarted,
          },
        },
        true,
      );

      if (generated.title) {
        await setSubject(generated.title);
      }

      if (generated.description) {
        const builder = new DeltaBuilder();
        builder.text(generated.description);
        await setDelta(builder);
      }

      await setIsLocked(!!generated.isLocked);

      openDrawer({ type: DRAWER_TYPE.COMPOSE_TASK });
    },
    [
      burstGenerateTaskQuery,
      createDraft,
      isGenerateTaskLoading,
      messageThread,
      openDrawer,
      setDelta,
      setIsLocked,
      setSubject,
    ],
  );

  if (!messageThreadId || !messageThread) {
    return null;
  }

  return (
    <DrawerContent w={rightWidth} maxW={rightWidth}>
      <DrawerTopHeader title="Select Messages" />
      <ForwardToSelectMessages
        draftId={TASK_DRAFT_ID}
        messageThread={messageThread}
        onNextClicked={onNextClicked}
        nextButtonLabel="Next"
        isNextLoading={isGenerateTaskLoading}
        disableNextIfNoneSelected
      />
    </DrawerContent>
  );
};

export default memo(GenerateTaskFromMessagesDrawer);
